import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Volume2,
  Play,
  SkipForward,
  Check,
  RotateCcw,
  Loader2,
  Sparkles,
  Download,
  FileText,
  Clock,
  Award,
  UploadCloud,
  ChevronDown,
  CheckCircle2,
  ArrowRight,
  Target,
  TrendingUp,
  AlertTriangle,
  AlertCircle,
  Lightbulb,
} from "lucide-react";
import PageHead from "@/components/PageHead";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STAR_CRITERIA = [
  { key: "situation", label: "Situation", desc: "Context & background" },
  { key: "task", label: "Task", desc: "Goal & challenge" },
  { key: "action", label: "Action", desc: "Key steps you took" },
  { key: "result", label: "Result", desc: "Impact & outcome" },
];

export default function FaceToFaceInterview() {
  const { user, refreshBalance } = useAuth();
  const candidateName = user?.name || "Candidate";
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  // Configuration State
  const [duration, setDuration] = useState("30 minutes");
  const [interviewType, setInterviewType] = useState("behavioral");
  const [difficulty, setDifficulty] = useState("medium");
  const [jd, setJd] = useState("");
  const [resumeText, setResumeText] = useState("");
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // Flow Stage: "setup" | "live" | "report"
  const [stage, setStage] = useState("setup");

  // Questions & Live State
  const [questions, setQuestions] = useState([]);
  const [qi, setQi] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [sessionResults, setSessionResults] = useState([]);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(null);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(false);

  // Final Report State
  const [report, setReport] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isExportingReport, setIsExportingReport] = useState(false);

  const timerRef = useRef(null);
  const recognitionRef = useRef(null);

  // Load User Career Profile Resume
  const loadProfile = () => {
    const p = user?.profile;
    if (!p) {
      toast.info("No profile found. Please upload a CV.");
      return;
    }
    const lines = [
      user?.name ? `Name: ${user.name}` : "",
      user?.email ? `Email: ${user.email}` : "",
      p.target_role ? `Target Role: ${p.target_role}` : "",
      p.current_role ? `Current Role: ${p.current_role}` : "",
      p.skills?.length ? `Skills: ${p.skills.join(", ")}` : "",
      p.bio ? `Summary: ${p.bio}` : "",
    ].filter(Boolean);

    setResumeText(lines.join("\n\n"));
    setFileName("Career_Profile_Resume");
    toast.success("Loaded resume from career profile!");
  };

  // Handle Resume Upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/ai/parse-resume", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = res.data?.text || res.data?.extracted_text || "";
      setResumeText(text);
      toast.success("Resume parsed successfully!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not parse resume. Please paste text directly.");
    } finally {
      setIsUploading(false);
    }
  };

  // Start Camera Stream
  const enableWebcam = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        setMediaStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setIsCameraOn(true);
      }
    } catch (err) {
      console.warn("Webcam access declined or not found:", err);
      setIsCameraOn(false);
    }
  };

  // Stop Camera Stream
  const stopWebcam = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach((t) => t.stop());
      setMediaStream(null);
    }
  };

  // Text-To-Speech Reader for Questions
  const speakQuestion = (text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1.0;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Toggle Camera
  const toggleCamera = () => {
    if (isCameraOn) {
      stopWebcam();
      setIsCameraOn(false);
      toast.info("Camera turned off.");
    } else {
      enableWebcam();
      toast.info("Camera turned on.");
    }
  };

  // Toggle Mic Speech-to-Text
  const toggleRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Speech recognition is not supported in this browser. Please type your answer.");
      return;
    }

    if (isMicOn) {
      recognitionRef.current?.stop();
      setIsMicOn(false);
      toast.info("Microphone muted.");
    } else {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onresult = (event) => {
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setUserAnswer((prev) => (prev ? prev + " " + transcript : transcript));
      };

      recognition.onerror = () => setIsMicOn(false);
      recognition.onend = () => setIsMicOn(false);

      recognitionRef.current = recognition;
      try {
        recognition.start();
        setIsMicOn(true);
        toast.info("Microphone active... Speak your response.");
      } catch (e) {
        console.warn("Recognition start error:", e);
      }
    }
  };

  // Start Face-to-Face Live Session
  const startSession = async () => {
    if (!duration) {
      toast.error("Interview Duration is mandatory. Please select a duration.");
      return;
    }
    if (!interviewType) {
      toast.error("Interview Type is mandatory. Please select an interview type.");
      return;
    }
    if (!difficulty) {
      toast.error("Difficulty Level is mandatory. Please select a difficulty.");
      return;
    }
    if (!resumeText.trim()) {
      toast.error("Resume is mandatory. Please upload your Resume (PDF/DOCX) or click 'Use Career Profile'.");
      return;
    }
    if (!jd.trim()) {
      toast.error("Job Description is mandatory. Please paste the target Job Description.");
      return;
    }

    setIsLoadingSession(true);
    try {
      const { data } = await api.post("/ai/interview-generate-qa-bank", {
        resume_text: resumeText,
        job_description: jd,
        duration,
        interview_type: interviewType,
        difficulty,
      });

      const qList = data?.questions || [];
      if (qList.length === 0) {
        throw new Error("No questions generated.");
      }
      setQuestions(qList);
      setQi(0);
      setUserAnswer("");
      setCurrentFeedback(null);
      setSessionResults([]);
      setStage("live");
      setSeconds(0);
      enableWebcam();
      refreshBalance?.();
      toast.success("Live Face-to-Face practice room initialized!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to initialize interview.");
    } finally {
      setIsLoadingSession(false);
    }
  };

  // Per-Question Timer Hook
  useEffect(() => {
    if (stage === "live") {
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } else {
      clearInterval(timerRef.current);
      stopWebcam();
    }
    return () => {
      clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, qi]);

  // Evaluate Single Answer with STAR
  const evaluateAnswer = async () => {
    if (!userAnswer.trim()) {
      toast.error("Please provide or speak your response before evaluating.");
      return;
    }

    const currentQ = questions[qi] || { question: "Interview Question" };
    setIsEvaluating(true);

    try {
      const { data } = await api.post("/ai/interview-feedback", {
        question: currentQ.question || currentQ.q,
        answer: userAnswer,
        role: user?.profile?.target_role || "Candidate",
      });

      setCurrentFeedback(data);
      const resultItem = {
        question: currentQ.question || currentQ.q,
        user_answer: userAnswer,
        ideal_answer: currentQ.ideal_answer || data?.model_answer,
        score: data?.overall || 80,
        star_scores: data?.star_scores || { situation: 80, task: 80, action: 80, result: 80 },
        strengths: data?.strengths || [],
        improvements: data?.improvements || [],
        section: currentQ.section || "General",
      };

      setSessionResults((prev) => [...prev, resultItem]);
      refreshBalance?.();
      toast.success("STAR analysis calculated!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not evaluate response.");
    } finally {
      setIsEvaluating(false);
    }
  };

  // Advance to next question or complete
  const nextQ = () => {
    if (qi < questions.length - 1) {
      setQi((i) => i + 1);
      setUserAnswer("");
      setCurrentFeedback(null);
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    } else {
      finishInterview();
    }
  };

  // Finish Interview & Compile Report
  const finishInterview = async () => {
    clearInterval(timerRef.current);
    stopWebcam();
    setStage("report");
    setIsGeneratingReport(true);

    let compiledResults = [...sessionResults];
    if (userAnswer.trim() && currentQ && !compiledResults.some((r) => r.question === (currentQ.question || currentQ.q))) {
      compiledResults.push({
        question: currentQ.question || currentQ.q,
        user_answer: userAnswer,
        ideal_answer: currentQ.ideal_answer || "",
        score: currentFeedback?.overall || 82,
        star_scores: currentFeedback?.star_scores || { situation: 80, task: 80, action: 80, result: 80 },
        strengths: currentFeedback?.strengths || [],
        improvements: currentFeedback?.improvements || [],
        section: currentQ.section || "General",
      });
    }

    try {
      const { data } = await api.post("/ai/interview-evaluate-session", {
        session_results: compiledResults,
        job_description: jd,
        resume_text: resumeText,
        duration,
      });
      setReport(data);
      toast.success("Performance report ready!");
    } catch (err) {
      console.error("Failed to generate report:", err);
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not generate session report.");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Export Report (PDF / DOCX)
  const exportPerformanceReport = async (fmt = "pdf") => {
    if (!report) return;
    setIsExportingReport(true);

    try {
      const response = await api.post(
        "/ai/interview-export-feedback-report",
        { report, format: fmt },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: fmt === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Face_To_Face_Interview_Report.${fmt}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded Report (${fmt.toUpperCase()})`);
    } catch (err) {
      console.error("Export report error:", err);
      toast.error(`Could not download .${fmt.toUpperCase()} report.`);
    } finally {
      setIsExportingReport(false);
    }
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(
    seconds % 60
  ).padStart(2, "0")}`;

  const currentQ = questions[qi] || {
    question: "Tell me about yourself and why you are excited for this role.",
  };

  return (
    <div className="max-w-6xl mx-auto pb-16 px-2 sm:px-4 space-y-6">
      <PageHead
        label="Face-to-Face Practice"
        title="Live Interactive Interview Practice Room"
        sub="Rehearse out loud with live webcam feedback, real-time speech transcription, and comprehensive AI STAR scoring."
      />

      {/* Credit Tier Chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card-surface p-4 rounded-2xl border border-border/80 text-center shadow-xs">
          <p className="text-base sm:text-lg font-bold text-electric">5 Credits</p>
          <p className="text-xs text-muted-foreground mt-0.5">15-minute session</p>
        </div>
        <div className="card-surface p-4 rounded-2xl border border-border/80 text-center shadow-xs">
          <p className="text-base sm:text-lg font-bold text-violet">8 Credits</p>
          <p className="text-xs text-muted-foreground mt-0.5">30-minute session</p>
        </div>
        <div className="card-surface p-4 rounded-2xl border border-border/80 text-center shadow-xs">
          <p className="text-base sm:text-lg font-bold text-coral">12 Credits</p>
          <p className="text-xs text-muted-foreground mt-0.5">45-minute session</p>
        </div>
        <div className="card-surface p-4 rounded-2xl border border-border/80 text-center shadow-xs">
          <p className="text-base sm:text-lg font-bold text-lime-600">🎙️ Live Voice</p>
          <p className="text-xs text-muted-foreground mt-0.5">Audio + Video Feedback</p>
        </div>
      </div>

      {/* =========================================================================
          STAGE 1: SETUP & CONFIGURATION
         ========================================================================= */}
      {stage === "setup" && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="card-surface p-6 shadow-card border border-border/90 rounded-2xl space-y-6">
            <div className="pb-3 border-b border-border/60">
              <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2">
                ⚙️ Live Mock Interview Setup
              </h3>
              <p className="text-xs text-muted-foreground">
                All fields marked with <span className="text-rose-500 font-bold">*</span> are mandatory to tailor your interview questions accurately.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-electric" /> Interview Duration <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      className="w-full appearance-none rounded-xl border border-border bg-card px-4 py-3 text-xs sm:text-sm font-medium text-foreground outline-none transition-all hover:border-electric/50 focus:border-electric pr-10 shadow-xs cursor-pointer"
                    >
                      <option value="15 minutes">15 minutes (5 Credits)</option>
                      <option value="30 minutes">30 minutes (8 Credits)</option>
                      <option value="45 minutes">45 minutes (12 Credits)</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground mb-1.5 block">
                    🎯 Interview Type <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setInterviewType("behavioral")}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        interviewType === "behavioral"
                          ? "border-violet bg-violet/10 text-foreground font-semibold shadow-xs"
                          : "border-border bg-card hover:bg-secondary text-muted-foreground text-xs"
                      }`}
                    >
                      <p className="text-xs sm:text-sm font-bold">🧠 Behavioral</p>
                      <p className="text-[11px] opacity-80 mt-0.5">STAR-format scenarios</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setInterviewType("technical")}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        interviewType === "technical"
                          ? "border-electric bg-electric/10 text-foreground font-semibold shadow-xs"
                          : "border-border bg-card hover:bg-secondary text-muted-foreground text-xs"
                      }`}
                    >
                      <p className="text-xs sm:text-sm font-bold">💻 Technical</p>
                      <p className="text-[11px] opacity-80 mt-0.5">Role competencies</p>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground mb-1.5 block">
                    📊 Difficulty Level <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {["easy", "medium", "hard"].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDifficulty(d)}
                        className={`py-2 px-3 rounded-xl border text-xs font-bold capitalize transition-all ${
                          difficulty === d
                            ? "border-amber-500 bg-amber-500/15 text-amber-600 dark:text-amber-300 shadow-xs"
                            : "border-border bg-card hover:bg-secondary text-muted-foreground"
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-foreground">
                      📋 Job Description <span className="text-rose-500 font-bold">*</span>
                    </label>
                    {!jd.trim() && (
                      <span className="text-[11px] text-rose-500 font-medium">Required</span>
                    )}
                  </div>
                  <textarea
                    rows={4}
                    value={jd}
                    onChange={(e) => setJd(e.target.value)}
                    placeholder="Paste the full job description or requirements here..."
                    className={`w-full resize-none rounded-xl border p-3 text-xs sm:text-sm leading-relaxed outline-none transition-colors ${
                      !jd.trim()
                        ? "border-border bg-background focus:border-electric"
                        : "border-emerald-500/50 bg-background focus:border-emerald-500"
                    }`}
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                      📄 Upload Your Resume (PDF / DOCX) <span className="text-rose-500 font-bold">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={loadProfile}
                      className="text-xs font-semibold text-electric hover:underline flex items-center gap-1"
                    >
                      <Sparkles className="h-3.5 w-3.5" /> Use Career Profile
                    </button>
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".pdf,.docx,.txt"
                    className="hidden"
                    id="f2f-resume-upload"
                  />
                  <label
                    htmlFor="f2f-resume-upload"
                    className={`flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3 text-xs font-medium transition-all ${
                      resumeText.trim()
                        ? "border-emerald-500/60 bg-emerald-500/10 text-foreground shadow-xs"
                        : "border-dashed border-border bg-secondary/40 hover:border-electric hover:bg-secondary text-muted-foreground"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 pr-2">
                      {isUploading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-electric shrink-0" />
                      ) : resumeText.trim() ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      ) : (
                        <UploadCloud className="h-4 w-4 text-electric shrink-0" />
                      )}
                      <span className="truncate">
                        {isUploading
                          ? "Extracting CV with AI…"
                          : fileName
                          ? `Resume: ${fileName}`
                          : resumeText.trim()
                          ? "Resume Profile Loaded"
                          : "Upload Resume File (PDF / DOCX) *"}
                      </span>
                    </div>
                    <span className="rounded-md bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground border border-border shadow-xs shrink-0">
                      {resumeText.trim() ? "Change" : "Browse"}
                    </span>
                  </label>
                </div>

                {/* Features Checklist */}
                <div className="rounded-2xl border border-border bg-secondary/30 p-4 sm:p-5 space-y-3">
                  <h4 className="font-heading text-xs sm:text-sm font-bold text-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-lime-600" /> What's Included
                  </h4>
                  <ul className="space-y-2 text-xs text-muted-foreground leading-relaxed">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                      <span>Live webcam preview & realistic practice simulation</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                      <span>AI voice question reader with TTS audio playback</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                      <span>Speech-to-text mic recording & per-question timer</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                      <span>Real-time STAR scoring and feedback report export</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Validation Banner if missing */}
            {(!resumeText.trim() || !jd.trim() || !duration || !interviewType || !difficulty) && (
              <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 px-3.5 py-2.5 rounded-xl">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  {!resumeText.trim() && !jd.trim()
                    ? "Resume and Job Description are mandatory to start the practice room."
                    : !resumeText.trim()
                    ? "Please upload your Resume or click 'Use Career Profile' to proceed."
                    : !jd.trim()
                    ? "Please enter the target Job Description to proceed."
                    : "Please ensure all configuration options are selected."}
                </span>
              </div>
            )}

            <div className="pt-2 border-t border-border/60">
              <button
                type="button"
                onClick={startSession}
                disabled={isLoadingSession || !jd.trim() || !resumeText.trim()}
                className="w-full cv-btn-primary py-3.5 px-6 text-sm font-bold flex items-center justify-center gap-2 shadow-glow disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isLoadingSession ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Preparing Practice Room…</span>
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 fill-white" />
                    <span>Enter Face-to-Face Practice Room</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          STAGE 2: LIVE FACE-TO-FACE PRACTICE ROOM
         ========================================================================= */}
      {stage === "live" && (
        <div className="overflow-hidden rounded-3xl border border-border bg-navy text-cream shadow-float">
          <div className="grid gap-0 lg:grid-cols-12">
            {/* Webcam / Candidate Stage */}
            <div className="relative flex flex-col items-center justify-center bg-gradient-to-br from-[#0d1530] to-[#131c3a] p-6 lg:col-span-7 lg:min-h-[460px]">
              {isCameraOn ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="h-full w-full max-h-[380px] rounded-2xl object-cover border border-white/10 shadow-2xl"
                />
              ) : (
                <div className="flex h-32 w-32 items-center justify-center rounded-full bg-electric/20 text-white font-heading text-4xl font-bold ring-4 ring-white/20">
                  {candidateName.charAt(0).toUpperCase()}
                </div>
              )}

              {/* Status Badges */}
              <div className="absolute left-6 top-6 flex items-center gap-2">
                <span className="flex items-center gap-2 rounded-full bg-red-500/90 px-3 py-1 text-xs font-semibold shadow-md">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-white" /> REC {mmss}
                </span>
                <span className="rounded-full bg-black/40 px-3 py-1 text-xs font-medium backdrop-blur">
                  Q{qi + 1} of {questions.length || 15}
                </span>
              </div>

              {/* Media Controls */}
              <div className="absolute bottom-6 right-6 flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={toggleCamera}
                  className={`flex h-10 w-10 items-center justify-center rounded-full transition-all shadow-md ${
                    isCameraOn
                      ? "bg-white/20 hover:bg-white/30 text-white border border-white/25"
                      : "bg-red-500/25 hover:bg-red-500/35 text-red-400 border border-red-500/40"
                  }`}
                  title={isCameraOn ? "Turn off Camera" : "Turn on Camera"}
                >
                  {isCameraOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5 text-red-400" />}
                </button>
                <button
                  type="button"
                  onClick={toggleRecording}
                  className={`flex h-10 w-10 items-center justify-center rounded-full transition-all shadow-md ${
                    isMicOn
                      ? "bg-emerald-500 text-white ring-2 ring-emerald-400/50 animate-pulse border border-emerald-400 shadow-emerald-500/20"
                      : "bg-red-500/25 hover:bg-red-500/35 text-red-400 border border-red-500/40"
                  }`}
                  title={isMicOn ? "Mute Microphone (Currently Active)" : "Unmute Microphone (Currently Muted)"}
                >
                  {isMicOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5 text-red-400" />}
                </button>
              </div>

              <span className="absolute bottom-6 left-6 rounded-full bg-black/40 px-3 py-1 text-xs backdrop-blur">
                {candidateName} · {user?.profile?.target_role || "Candidate"}
              </span>
            </div>

            {/* Question & Feedback Stage */}
            <div className="flex flex-col justify-between p-6 lg:col-span-5 bg-navy/90 border-t lg:border-t-0 lg:border-l border-white/10 space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-aqua">
                    {currentQ.section || "Core Competency"}
                  </span>
                  <button
                    onClick={() => speakQuestion(currentQ.question || currentQ.q)}
                    className="flex items-center gap-1 text-xs font-semibold text-aqua hover:underline"
                  >
                    <Volume2 className="h-3.5 w-3.5" /> Hear Prompt
                  </button>
                </div>

                <h3 className="font-heading text-base sm:text-lg font-bold text-cream leading-relaxed">
                  "{currentQ.question || currentQ.q}"
                </h3>

                <textarea
                  rows={4}
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder="Speak into microphone or type your response using STAR..."
                  className="w-full resize-none rounded-xl border border-white/15 bg-white/5 p-3 text-xs sm:text-sm text-cream leading-relaxed outline-none focus:border-aqua"
                />

                <div className="flex items-center justify-between">
                  <button
                    onClick={evaluateAnswer}
                    disabled={isEvaluating || !userAnswer.trim()}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-aqua/20 border border-aqua/30 px-3 py-1.5 text-xs font-bold text-aqua transition-colors hover:bg-aqua/30"
                  >
                    {isEvaluating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    <span>Assess STAR</span>
                  </button>
                </div>

                {/* Instant Assessment Snippet */}
                {currentFeedback && (
                  <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2 text-xs">
                    <p className="font-bold text-aqua">
                      Score: {currentFeedback.overall || 80}/100
                    </p>
                    <div className="grid grid-cols-4 gap-1 text-center text-[10px]">
                      {STAR_CRITERIA.map(({ key, label }) => (
                        <div key={key} className="bg-white/5 p-1 rounded">
                          <span className="text-cream/60">{label.charAt(0)}</span>
                          <p className="font-bold text-aqua">{currentFeedback.star_scores?.[key] || 80}%</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Action */}
              <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                <button
                  onClick={() => setStage("setup")}
                  className="text-xs text-cream/60 hover:text-cream underline"
                >
                  Exit Room
                </button>
                <button
                  onClick={nextQ}
                  data-testid="f2f-next"
                  className="inline-flex items-center gap-2 rounded-xl bg-cream px-5 py-2.5 font-bold text-navy shadow-xs transition-transform hover:scale-105"
                >
                  <span>{qi < questions.length - 1 ? "Next Question" : "Finish Session"}</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          STAGE 3: PERFORMANCE REPORT
         ========================================================================= */}
      {stage === "report" && (
        <div className="card-surface p-6 sm:p-8 shadow-card border border-border/90 rounded-2xl space-y-8 animate-in fade-in duration-300">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-5 border-b border-border/60">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-electric/15 text-electric">
                  <Award className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-heading text-lg sm:text-xl font-bold text-foreground">
                    Face-to-Face Assessment Report
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Candidate: <span className="font-medium text-foreground">{candidateName}</span> · Duration: {duration} · Role: {user?.profile?.target_role || "Target Role"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => exportPerformanceReport("pdf")}
                disabled={isExportingReport}
                className="cv-btn-primary py-2.5 px-4 text-xs font-semibold flex items-center gap-1.5 shadow-glow cursor-pointer"
              >
                {isExportingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                <span>Download Report (PDF)</span>
              </button>
              <button
                type="button"
                onClick={() => exportPerformanceReport("docx")}
                disabled={isExportingReport}
                className="rounded-xl border border-border bg-card hover:bg-secondary text-foreground py-2.5 px-4 text-xs font-semibold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
              >
                {isExportingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 text-sky-400" />}
                <span>DOCX</span>
              </button>
            </div>
          </div>

          {/* Scores Overview Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
            <div className="card-surface p-4 rounded-xl border border-border text-center bg-card/60 hover:border-electric/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Overall Score</p>
              <p className="text-3xl font-extrabold text-electric mt-1.5">{report?.overall_score || 80}<span className="text-xs font-medium text-muted-foreground">/100</span></p>
            </div>
            <div className="card-surface p-4 rounded-xl border border-border text-center bg-card/60 hover:border-violet/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">General / STAR</p>
              <p className="text-3xl font-extrabold text-violet mt-1.5">{report?.general_score || 80}<span className="text-xs font-medium text-muted-foreground">/100</span></p>
            </div>
            <div className="card-surface p-4 rounded-xl border border-border text-center bg-card/60 hover:border-sky-500/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Technical</p>
              <p className="text-3xl font-extrabold text-sky-500 mt-1.5">{report?.technical_score || 80}<span className="text-xs font-medium text-muted-foreground">/100</span></p>
            </div>
            <div className="card-surface p-4 rounded-xl border border-border text-center bg-card/60 hover:border-emerald-500/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Resume Match</p>
              <p className="text-3xl font-extrabold text-emerald-500 mt-1.5">{report?.resume_score || 80}<span className="text-xs font-medium text-muted-foreground">/100</span></p>
            </div>
            <div className="card-surface p-4 rounded-xl border border-border text-center col-span-2 sm:col-span-1 bg-card/60 hover:border-lime-500/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Hiring Band</p>
              <p className="text-lg font-bold text-lime-600 dark:text-lime-400 mt-2.5">{report?.performance_band || "Strong Hire"}</p>
            </div>
          </div>

          {/* Executive Summary */}
          <div className="rounded-2xl bg-secondary/30 border border-border/80 p-5 sm:p-6 space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-electric">
              <Sparkles className="h-4 w-4" />
              <span>Overall Assessment</span>
            </div>
            <p className="text-xs sm:text-sm text-foreground leading-relaxed">
              {report?.overall_summary || "Demonstrated structured communication, good domain understanding, and solid alignment with role requirements."}
            </p>
          </div>

          {/* Strengths & Weaknesses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Key Strengths */}
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/20 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <h4 className="font-heading text-sm font-bold text-emerald-600 dark:text-emerald-400">
                  Key Strengths
                </h4>
              </div>
              <ul className="space-y-2 text-xs text-foreground/90">
                {(report?.key_strengths || [
                  "Confident verbal communication and logical thought progression",
                  "Solid articulation of technical decisions and project ownership",
                ]).map((s, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-emerald-500 font-bold mt-0.5">•</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas to Improve */}
            <div className="rounded-2xl bg-amber-500/10 border border-amber-500/20 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <h4 className="font-heading text-sm font-bold text-amber-600 dark:text-amber-400">
                  Areas for Enhancement
                </h4>
              </div>
              <ul className="space-y-2 text-xs text-foreground/90">
                {(report?.weak_areas || [
                  "Incorporate quantifiable business impact metrics (e.g. latency reduction %, revenue saved)",
                  "Structure behavioral responses strictly using Situation-Task-Action-Result format",
                ]).map((w, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-amber-500 font-bold mt-0.5">•</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Detailed Competency Feedback Breakdown */}
          {(report?.general_feedback || report?.technical_feedback || report?.resume_feedback) && (
            <div className="space-y-3">
              <h4 className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-electric" /> Competency Breakdown
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                {report?.general_feedback && (
                  <div className="rounded-xl border border-border bg-card/60 p-4 space-y-1.5">
                    <p className="font-semibold text-foreground flex items-center gap-1.5">
                      💬 Behavioral / STAR
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      {report.general_feedback}
                    </p>
                  </div>
                )}
                {report?.technical_feedback && (
                  <div className="rounded-xl border border-border bg-card/60 p-4 space-y-1.5">
                    <p className="font-semibold text-foreground flex items-center gap-1.5">
                      ⚙️ Technical Depth
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      {report.technical_feedback}
                    </p>
                  </div>
                )}
                {report?.resume_feedback && (
                  <div className="rounded-xl border border-border bg-card/60 p-4 space-y-1.5">
                    <p className="font-semibold text-foreground flex items-center gap-1.5">
                      📄 Resume Alignment
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      {report.resume_feedback}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actionable Recommendations & Next Steps */}
          {((report?.recommendations && report.recommendations.length > 0) || report?.next_steps) && (
            <div className="rounded-2xl border border-border bg-secondary/20 p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-400" />
                <h4 className="font-heading text-sm font-bold text-foreground">
                  Actionable Recommendations & Next Steps
                </h4>
              </div>
              {report?.recommendations && report.recommendations.length > 0 && (
                <ul className="space-y-2 text-xs text-foreground/90">
                  {report.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-electric font-bold mt-0.5">•</span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              )}
              {report?.next_steps && (
                <div className="pt-3 border-t border-border/50 text-xs">
                  <span className="font-semibold text-foreground">Next Strategic Step: </span>
                  <span className="text-muted-foreground">{report.next_steps}</span>
                </div>
              )}
            </div>
          )}

          {/* Keywords Match Analysis */}
          {((report?.keywords_covered && report.keywords_covered.length > 0) || (report?.keywords_missed && report.keywords_missed.length > 0)) && (
            <div className="space-y-3 pt-2">
              <h4 className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
                <Target className="h-4 w-4 text-electric" /> Target Job Keyword Match
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                {report?.keywords_covered && report.keywords_covered.length > 0 && (
                  <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-2">
                    <p className="font-semibold text-emerald-600 dark:text-emerald-400">Covered Keywords ({report.keywords_covered.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {report.keywords_covered.slice(0, 15).map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-medium text-[11px]">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {report?.keywords_missed && report.keywords_missed.length > 0 && (
                  <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-2">
                    <p className="font-semibold text-amber-600 dark:text-amber-400">Suggested Keywords to Highlight ({report.keywords_missed.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {report.keywords_missed.slice(0, 15).map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium text-[11px]">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Questions Practiced Breakdown */}
          {sessionResults && sessionResults.length > 0 && (
            <div className="space-y-3 pt-2">
              <h4 className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-electric" /> Evaluated Questions ({sessionResults.length})
              </h4>
              <div className="space-y-3">
                {sessionResults.map((item, idx) => (
                  <div key={idx} className="rounded-xl border border-border bg-card/60 p-4 space-y-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-foreground">
                        Q{idx + 1}: {item.question}
                      </p>
                      <span className="px-2.5 py-0.5 rounded-full bg-electric/15 text-electric font-bold text-[11px]">
                        {item.score || 80}/100
                      </span>
                    </div>
                    {item.user_answer && (
                      <p className="text-muted-foreground bg-secondary/30 p-2.5 rounded-lg italic">
                        "{item.user_answer}"
                      </p>
                    )}
                    {item.improvements && item.improvements.length > 0 && (
                      <div className="text-[11px] text-amber-500 space-y-0.5">
                        <span className="font-semibold">Coaching Note: </span>
                        {item.improvements.join("; ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Row */}
          <div className="pt-4 flex items-center justify-between border-t border-border/60">
            <button
              type="button"
              onClick={() => {
                setStage("setup");
                setSessionResults([]);
                setReport(null);
                setQi(0);
                setUserAnswer("");
              }}
              className="rounded-xl border border-border bg-card hover:bg-secondary text-foreground py-3 px-5 text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-xs cursor-pointer"
            >
              <RotateCcw className="h-4 w-4" /> Start New Practice Session
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

